import { fadeTransition } from '@utility/config/animations';
import useModal from '@utility/hooks/useModal';
import classNames from 'classnames';
import { motion } from 'motion/react';
import { PropsWithChildren, RefObject, useEffect, useRef } from 'react';
import Portal from './Portal';

export interface IModal {
  readonly containerClasses?: string;
  readonly isOpen: boolean;
  readonly setIsOpen: (state: boolean) => void;
  readonly restoreFocusRef: RefObject<HTMLButtonElement | null>;
}

const Modal = ({
  children,
  containerClasses,
  isOpen,
  setIsOpen,
  restoreFocusRef,
}: PropsWithChildren<IModal>) => {
  const targetRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const modalPreviouslyOpen = useRef<boolean>(isOpen);

  useModal(isOpen, setIsOpen, targetRef);

  const stopPropagation = (event: React.MouseEvent<HTMLDivElement>) =>
    event.target === contentRef.current
      ? setIsOpen(false)
      : event.stopPropagation();

  useEffect(() => {
    // Restore focus to the element that was focused before the modal opened.
    // restoreFocusRef should be attached to the button that opened the modal.
    const wasPreviouslyOpened = modalPreviouslyOpen.current;
    const elementToRestoreFocusToOnClose = restoreFocusRef.current;

    if (wasPreviouslyOpened && !isOpen && elementToRestoreFocusToOnClose) {
      elementToRestoreFocusToOnClose.focus();
    }

    modalPreviouslyOpen.current = isOpen;
  }, [isOpen]);

  return isOpen ? (
    <Portal key="portal" mountId="portal-mount">
      <motion.div
        id="page-modal"
        role="dialog"
        aria-modal
        initial={{ opacity: 0 }}
        animate={{ opacity: isOpen ? 1 : 0 }}
        transition={fadeTransition}
        exit={{ height: 'auto' }}
        onClick={() => setIsOpen(false)}
        ref={targetRef}
        tabIndex={0}
        className="tw-fixed tw-inset-0 tw-z-max tw-overflow-auto tw-bg-black/80 tw-outline-none"
      >
        <div
          className={classNames('tw-grid-container', containerClasses)}
          onClick={stopPropagation}
          ref={contentRef}
        >
          {children}
        </div>
      </motion.div>
    </Portal>
  ) : null;
};

export default Modal;
